import React, { useEffect, useState } from 'react';
import {
  RangeCalendarSingle,
  formatPickerDate,
  toYmd,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';

function isBusinessDayLocal(d) {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function getNextBusinessDayFromLocalToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(today);
  d.setDate(d.getDate() + 1);
  while (!isBusinessDayLocal(d)) {
    d.setDate(d.getDate() + 1);
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

function getMinAllowedFutureDate() {
  const nextBd = getNextBusinessDayFromLocalToday();
  const min = new Date(nextBd);
  min.setDate(min.getDate() + 1);
  min.setHours(0, 0, 0, 0);
  return min;
}

function compareDatesOnly(date1, date2) {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1.getTime() - d2.getTime();
}

function validateFutureDate(dateValue) {
  if (!dateValue) return '';
  const [year, month, day] = dateValue.split('-').map(Number);
  const selected = new Date(year, month - 1, day);
  selected.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (compareDatesOnly(selected, today) <= 0) {
    return 'A data não pode ser hoje ou anterior';
  }
  const minAllowedDate = getMinAllowedFutureDate();
  if (compareDatesOnly(selected, minAllowedDate) < 0) {
    return 'A data deve ser pelo menos um dia após o próximo dia útil';
  }
  return '';
}

export default function FutureDateModal({
  isOpen,
  data,
  onClose,
  onConfirm,
  overlayClassName = '',
}) {
  const [selectedDate, setSelectedDate] = useState('');
  const [noDate, setNoDate] = useState(false);
  const [dateError, setDateError] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [observacoesError, setObservacoesError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedDate('');
      setNoDate(false);
      setDateError('');
      setObservacoes('');
      setObservacoesError('');
    }
  }, [isOpen, data]);

  const minAllowed = getMinAllowedFutureDate();
  const minDate = toYmd(minAllowed);
  const maxDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 5);
    return toYmd(d);
  })();

  const handleNoDateChange = (e) => {
    setNoDate(e.target.checked);
    if (e.target.checked) {
      setSelectedDate('');
      setDateError('');
    }
  };

  const handleDateSelect = (dateStr) => {
    if (noDate || !dateStr) return;
    const err = validateFutureDate(dateStr);
    if (err) {
      setDateError(err);
      return;
    }
    setDateError('');
    setSelectedDate(dateStr);
  };

  const handleConfirm = () => {
    if (!noDate && !selectedDate) {
      setDateError('Selecione uma data ou marque "Não há data certa de previsão"');
      return;
    }
    if (!noDate && dateError) {
      return;
    }
    const obsTrim = String(observacoes || '').trim();
    if (noDate && obsTrim.length < 10) {
      setObservacoesError('Observações obrigatórias (mínimo 10 caracteres)');
      return;
    }
    setObservacoesError('');
    onConfirm(selectedDate || null, noDate, obsTrim);
  };

  if (!isOpen || !data) {
    return null;
  }

  const orderCount = data.orderIds?.length || 0;
  const overlayCls = ['modal-overlay', overlayClassName].filter(Boolean).join(' ');

  return (
    <div className={overlayCls} onClick={onClose}>
      <div className="future-date-modal" onClick={(e) => e.stopPropagation()}>
        <div className="future-date-modal-header">
          <h3>Definir Previsão</h3>
          <button type="button" className="future-date-modal-close" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="future-date-modal-body">
          <p className="future-date-modal-info">
            {orderCount === 1
              ? 'Defina a data de previsão para a ordem de serviço:'
              : `Defina a data de previsão para ${orderCount} ordens de serviço:`}
          </p>

          <div className="future-date-calendar-container">
            <label className="future-date-label">Data de Previsão</label>
            <p
              className={`future-date-selected-range${!selectedDate ? ' future-date-selected-range--empty' : ''}`}
            >
              {selectedDate ? formatPickerDate(selectedDate) : 'Selecione a data'}
            </p>
            <div className={`future-date-period-wrap${noDate ? ' is-disabled' : ''}`}>
              <div className="ec-period-picker-body future-date-period-body">
                <RangeCalendarSingle
                  minDate={minDate}
                  maxDate={maxDate}
                  startDate={noDate ? null : selectedDate || null}
                  endDate={noDate ? null : selectedDate || null}
                  dayMode
                  initialMonthDate={minDate}
                  onDateSelect={handleDateSelect}
                />
              </div>
            </div>
            {dateError ? <span className="future-date-error">{dateError}</span> : null}
          </div>

          <div className="future-date-checkbox-container">
            <label className="future-date-checkbox-label">
              <input
                type="checkbox"
                className="deq-check-native"
                checked={noDate}
                onChange={handleNoDateChange}
              />
              <span className="deq-check-box" aria-hidden="true" />
              <span>Não há data certa de previsão</span>
            </label>
          </div>

          <div className="future-date-observacoes-container">
            <label className="future-date-label" htmlFor="future-date-observacoes">
              Observações
            </label>
            <textarea
              id="future-date-observacoes"
              className={`future-date-textarea ${observacoesError ? 'error' : ''}`}
              rows={3}
              placeholder="Sem data: obrigatório (mín. 10 caracteres). Com data: opcional."
              value={observacoes}
              onChange={(e) => {
                setObservacoes(e.target.value);
                setObservacoesError('');
              }}
            />
            {observacoesError ? (
              <span className="future-date-error">{observacoesError}</span>
            ) : null}
          </div>
        </div>

        <div className="future-date-modal-footer">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={handleConfirm}
            disabled={
              noDate
                ? String(observacoes || '').trim().length < 10
                : !selectedDate || !!dateError
            }
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
